'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Eye,
  FileWarning,
  History,
  ImagePlus,
  LoaderCircle,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { readWikiToken } from '../lib/wiki-auth';
import { uploadHomepageImage } from '../lib/homepage-config';
import {
  DEFAULT_CATALOG_TEMPLATE_VERSION,
  type CatalogTemplateSettings,
} from '../lib/catalog-template-config';

type ChangeType = 'added' | 'updated' | 'removed';
type CatalogStatus = ChangeType | 'unchanged';
type CatalogFilter = 'all' | 'changed' | CatalogStatus;
type CatalogChange = {
  id: string;
  name: string;
  type: CatalogStatus;
  sourceUpdatedAt?: string;
  currentUpdatedAt?: string;
  reasons?: string[];
};
type CheckResult = {
  sourceUrl: string;
  schemaVersion: number | string;
  generatedAt?: string;
  checkedAt: string;
  sourceCount: number;
  currentCount: number;
  summary: Record<ChangeType | 'unchanged', number>;
  changes: CatalogChange[];
  unchangedHeroes?: CatalogChange[];
};
type SyncBatch = {
  batchId: string;
  status: 'running' | 'applied' | 'failed' | 'rolled_back';
  added: number;
  updated: number;
  archived: number;
  createdBy?: string;
  createdAt: string;
  rolledBackAt?: string | null;
};
type SyncProgress = {
  status: 'queued' | 'running' | 'applied' | 'failed';
  stage: string;
  percent: number;
  current: number;
  total: number;
  message: string;
  completedHeroIds?: string[];
  completedArchiveIds?: string[];
  remainingHeroIds?: string[];
  remainingArchiveIds?: string[];
};
type SyncRequest = { heroIds: string[]; archiveIds: string[] };
type ManagedCatalog = {
  catalogKey: string;
  iconUrl?: string;
  displayMode: 'standalone' | 'modal';
  name: string;
  sourceUrl: string;
  adapterKey: string;
  detailPrefix?: string;
  hasIndependentCode?: boolean;
  settings: CatalogTemplateSettings;
};

type GenericCheck = {
  sourceUrl: string;
  digest: string;
  sourceCount: number;
  currentCount: number;
  summary: { added: number; updated: number; unchanged: number };
  changes: CatalogChange[];
};
type GenericBatch = { batchId: string; createdAt: string; createdBy: string; count: number; rolledBackAt?: string | null };
type GenericTaskItem = { sourceId: string; dbId?: string; name: string; type: 'added' | 'updated' | 'source_missing' | 'mapping_error'; reason?: string; detail?: unknown; resources?: string[] };
type GenericTask = { taskId: string; status: string; result: GenericTaskItem[]; selectedIds: string[]; errorMessage?: string };

const isLegacyCardCatalog = (gameKey: string, catalogKey: string) =>
  gameKey === 'arcanatactics' && (
    catalogKey === 'catalog_65fcfc5c37224d95987a03711aec9e67' ||
    catalogKey === 'catalog_ab547affe0d548a4bcdea0fb4ee1d12e'
  );

function CatalogIconUpload({ gameKey, catalog, onSaved }: {
  gameKey: string;
  catalog: ManagedCatalog;
  onSaved: (url: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const endpoint = `/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(catalog.catalogKey)}/icon`;
  const showSavedIcon = (iconUrl: string) => {
    onSaved(iconUrl);
    window.dispatchEvent(new CustomEvent('game-wiki-catalog-icon-change', {
      detail: { catalogKey: catalog.catalogKey, iconUrl },
    }));
  };
  const upload = async (file?: File) => {
    if (!file) return;
    const token = readWikiToken();
    if (!token) return setError('登录状态已失效，请重新登录。');
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const iconUrl = await uploadHomepageImage(gameKey, file, token);
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json', 'access-token': token },
        body: JSON.stringify({ iconUrl }),
      });
      const payload = (await response.json().catch(() => ({}))) as { error?: string; iconUrl?: string };
      if (!response.ok) throw new Error(`${payload.error || '图鉴图片保存失败'}（HTTP ${response.status}）`);
      if (!payload.iconUrl) throw new Error('图鉴图片保存成功，但未返回 CDN 地址');
      showSavedIcon(payload.iconUrl);
      setMessage('图鉴图片已保存');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '图鉴图片上传失败');
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    const token = readWikiToken();
    if (!token) return setError('登录状态已失效，请重新登录。');
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(endpoint, { method: 'DELETE', headers: { 'access-token': token } });
      const payload = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) throw new Error(`${payload.error || '删除图片失败'}（HTTP ${response.status}）`);
      showSavedIcon('');
      setMessage('图鉴图片已删除');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '删除图片失败');
    } finally {
      setBusy(false);
    }
  };
  return <span className="catalog-icon-upload-wrap">
    <button className={`catalog-source-icon catalog-icon-upload${catalog.iconUrl ? ' has-image' : ''}`} type="button" title={`上传${catalog.name}图片`} aria-label={`上传${catalog.name}图片`} disabled={busy} onClick={() => inputRef.current?.click()}>
      {catalog.iconUrl && <img src={catalog.iconUrl} alt="" />}
      <ImagePlus className="catalog-icon-upload-badge" aria-hidden="true" />
      {busy && <LoaderCircle className="is-spinning catalog-icon-upload-spinner" />}
    </button>
    <input ref={inputRef} type="file" hidden aria-label={`上传${catalog.name}图片`} accept="image/jpeg,image/png,image/webp,image/gif,image/avif" disabled={busy} onChange={(event) => {
      void upload(event.target.files?.[0]);
      event.target.value = '';
    }} />
    {catalog.iconUrl && <button className="catalog-icon-remove" type="button" disabled={busy} onClick={() => void remove()} aria-label={`删除${catalog.name}图片`} title="删除图片"><Trash2 /></button>}
    {(error || message) && <span className={`catalog-icon-feedback${error ? ' is-error' : ''}`} role={error ? 'alert' : 'status'}>{error || message}</span>}
  </span>;
}
type SharedCatalogTemplate = {
  templateKey: string;
  name: string;
  catalogKind: string;
  sourceGameKey: string;
  sourceCatalogKey: string;
  sourceTemplateId: string;
};
type CatalogPreview = {
  checkedAt: string;
  sourceHash: string;
  wikiHash: string;
  currentPresent: boolean;
  currentUpdatedAt?: string;
  hero: {
    id: string;
    name: string;
    stars: number;
    head: string;
    story?: string;
    traits: Array<{ label: string; name: string }>;
    stats: Array<{
      key: string;
      label: string;
      value: number;
      suffix?: string;
    }>;
    skills: Array<{
      id: string;
      name: string;
      ranks: string[];
      upgradeLevels: number[];
    }>;
    forms: Array<{ id: string; name: string; fallback?: string }>;
    quotes: string[];
  };
  warnings: string[];
  ignoredFields: string[];
  resources: Array<{ kind: string; sourceUrl: string; owner: string }>;
  summary: {
    stats: number;
    skills: number;
    forms: number;
    quotes: number;
    resources: number;
    warnings: number;
  };
};

const DEFAULT_SOURCE =
  'http://10.22.0.61:8001/arcanatactics_heroes/manifest.json';
const changeLabels: Record<ChangeType, string> = {
  added: '新增',
  updated: '有更新',
  removed: '上游移除',
};
const statusLabels: Record<CatalogStatus, string> = {
  ...changeLabels,
  unchanged: '无变化',
};

function createBatchId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (token) => {
    const value = Math.floor(Math.random() * 16);
    return (token === 'x' ? value : (value & 0x3) | 0x8).toString(16);
  });
}

export default function CatalogAdminPanel({ gameKey }: { gameKey: string }) {
  const [templateSettings, setTemplateSettings] =
    useState<CatalogTemplateSettings>({
      detailTemplate: 'default',
      updatePolicy: 'follow',
      templateVersion: DEFAULT_CATALOG_TEMPLATE_VERSION,
    });
  const [persistedTemplateSettings, setPersistedTemplateSettings] =
    useState<CatalogTemplateSettings>({
      detailTemplate: 'default',
      updatePolicy: 'follow',
      templateVersion: DEFAULT_CATALOG_TEMPLATE_VERSION,
    });
  const [hasCustomTemplate, setHasCustomTemplate] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateMessage, setTemplateMessage] = useState('');
  const [sourceUrl, setSourceUrl] = useState(DEFAULT_SOURCE);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<CheckResult | null>(null);
  const [filter, setFilter] = useState<CatalogFilter>('changed');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<SyncBatch[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [previewingId, setPreviewingId] = useState('');
  const [preview, setPreview] = useState<CatalogPreview | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [retryRequest, setRetryRequest] = useState<SyncRequest | null>(null);
  const [rollingBackId, setRollingBackId] = useState('');
  const [catalogs, setCatalogs] = useState<ManagedCatalog[]>([]);
  const savedCatalogsRef = useRef<Record<string, ManagedCatalog>>({});
  const [heroCatalogPresent, setHeroCatalogPresent] = useState(gameKey === 'arcanatactics');
  const [templateModalKey, setTemplateModalKey] = useState<string | null>(null);
  const [sharedTemplates, setSharedTemplates] = useState<
    SharedCatalogTemplate[]
  >([]);
  const [saveSharedOpen, setSaveSharedOpen] = useState(false);
  const [sharedTemplateName, setSharedTemplateName] = useState('');
  const [sharedTemplateSaving, setSharedTemplateSaving] = useState(false);
  const [sharedTemplateError, setSharedTemplateError] = useState('');
  const [addCatalogOpen, setAddCatalogOpen] = useState(false);
  const [addCatalogSaving, setAddCatalogSaving] = useState(false);
  const [addCatalogError, setAddCatalogError] = useState('');
  const [additionalCatalogSaving, setAdditionalCatalogSaving] = useState('');
  const [renamingCatalogKey, setRenamingCatalogKey] = useState('');
  const [catalogNameDraft, setCatalogNameDraft] = useState('');
  const [catalogRenameBusy, setCatalogRenameBusy] = useState(false);
  const [catalogRenameError, setCatalogRenameError] = useState('');
  const [additionalCatalogMessage, setAdditionalCatalogMessage] = useState<{ catalogKey: string; text: string }>({ catalogKey: '', text: '' });
  const [checkingCatalogKey, setCheckingCatalogKey] = useState('');
  const [catalogCheckMessage, setCatalogCheckMessage] = useState<Record<string, string>>({});
  const [cardImport, setCardImport] = useState<{ catalog: ManagedCatalog; digest: string; count: number } | null>(null);
  const [cardImportBusy, setCardImportBusy] = useState(false);
  const [cardImportError, setCardImportError] = useState('');
  const [genericCheck, setGenericCheck] = useState<{ catalog: ManagedCatalog; result: GenericCheck } | null>(null);
  const [genericSelected, setGenericSelected] = useState<string[]>([]);
  const [genericBusy, setGenericBusy] = useState(false);
  const [genericError, setGenericError] = useState('');
  const [genericHistory, setGenericHistory] = useState<{ catalog: ManagedCatalog; items: GenericBatch[] } | null>(null);
  const [genericTask, setGenericTask] = useState<{ catalog: ManagedCatalog; task: GenericTask } | null>(null);
  const [genericTaskSelected, setGenericTaskSelected] = useState<string[]>([]);
  const [genericTaskJSON, setGenericTaskJSON] = useState<GenericTaskItem | null>(null);
  const [catalogDeleteTarget, setCatalogDeleteTarget] = useState<ManagedCatalog | null>(null);
  const [catalogDeleteBusy, setCatalogDeleteBusy] = useState(false);
  const [catalogDeleteError, setCatalogDeleteError] = useState('');
  const [newCatalog, setNewCatalog] = useState({
    displayMode: 'standalone' as 'standalone' | 'modal',
    name: '',
    settings: { listShape: 'square' as NonNullable<CatalogTemplateSettings['listShape']> },
  });
  const loadCatalogs = async () => {
    const response = await fetch(
      `/api/games/${encodeURIComponent(gameKey)}/catalogs`,
      { cache: 'no-store' },
    );
    const payload = (await response.json()) as {
      catalogs?: ManagedCatalog[];
      error?: string;
    };
    if (!response.ok) throw new Error(payload.error || '图鉴列表读取失败');
    const loadedCatalogs = payload.catalogs || [];
    savedCatalogsRef.current = Object.fromEntries(loadedCatalogs.map((catalog) => [catalog.catalogKey, catalog]));
    setCatalogs(loadedCatalogs);
    setHeroCatalogPresent(gameKey === 'arcanatactics' && loadedCatalogs.some((catalog) => catalog.catalogKey === 'hero' && catalog.adapterKey !== 'generic-json-v1'));
  };
  useEffect(() => {
    let active = true;
    setTemplateLoading(true);
    fetch(
      `/api/games/${encodeURIComponent(gameKey)}/catalog-settings?catalog=hero`,
      {
        cache: 'no-store',
      },
    )
      .then(async (response) => {
        const payload = (await response.json()) as {
          settings?: CatalogTemplateSettings;
          hasCustomTemplate?: boolean;
          error?: string;
        };
        if (!response.ok)
          throw new Error(payload.error || '图鉴模板设置读取失败');
        if (active && payload.settings) {
          setTemplateSettings(payload.settings);
          setPersistedTemplateSettings(payload.settings);
          setHasCustomTemplate(Boolean(payload.hasCustomTemplate));
        }
      })
      .catch((reason) => {
        if (active)
          setTemplateMessage(
            reason instanceof Error ? reason.message : '图鉴模板设置读取失败',
          );
      })
      .finally(() => {
        if (active) setTemplateLoading(false);
      });
    return () => {
      active = false;
    };
  }, [gameKey]);
  useEffect(() => {
    void loadCatalogs().catch((reason) =>
      setAddCatalogError(
        reason instanceof Error ? reason.message : '图鉴列表读取失败',
      ),
    );
  }, [gameKey]);
  useEffect(() => {
    fetch('/api/catalog-templates?catalogKind=hero', { cache: 'no-store' })
      .then(async (response) => {
        const payload = (await response.json()) as {
          templates?: SharedCatalogTemplate[];
        };
        if (response.ok) setSharedTemplates(payload.templates || []);
      })
      .catch(() => undefined);
  }, []);
  const openAddCatalog = () => {
    setAddCatalogError('');
    setNewCatalog({
      displayMode: 'standalone',
      name: '',
      settings: { listShape: 'square' },
    });
    setAddCatalogOpen(true);
  };
  const createCatalog = async () => {
    const token = readWikiToken();
    if (!token) {
      setAddCatalogError('登录状态已失效，请重新登录。');
      return;
    }
    setAddCatalogSaving(true);
    setAddCatalogError('');
    try {
      const response = await fetch(
        `/api/games/${encodeURIComponent(gameKey)}/catalogs`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'access-token': token,
          },
          body: JSON.stringify(newCatalog),
        },
      );
      const payload = (await response.json().catch(() => null)) as {
        catalog?: ManagedCatalog;
        error?: string;
      } | null;
      if (!payload) throw new Error(`图鉴服务响应异常（HTTP ${response.status}），请稍后重试`);
      if (!response.ok) throw new Error(payload.error || '新增图鉴失败');
      if (payload.catalog)
        {
          savedCatalogsRef.current[payload.catalog.catalogKey] = payload.catalog;
          setCatalogs((current) => [...current, payload.catalog!]);
        }
      setAddCatalogOpen(false);
    } catch (reason) {
      setAddCatalogError(
        reason instanceof Error ? reason.message : '新增图鉴失败',
      );
    } finally {
      setAddCatalogSaving(false);
    }
  };
  const updateAdditionalCatalog = (
    catalogKey: string,
    update: (catalog: ManagedCatalog) => ManagedCatalog,
  ) =>
    setCatalogs((current) =>
      current.map((catalog) =>
        catalog.catalogKey === catalogKey ? update(catalog) : catalog,
      ),
    );
  const renameCatalog = async (catalog: ManagedCatalog) => {
    const name = catalogNameDraft.trim();
    if (!name || [...name].length > 120) {
      setCatalogRenameError('请输入不超过120字的图鉴名称。');
      return;
    }
    if (name === catalog.name) {
      setRenamingCatalogKey('');
      return;
    }
    const token = readWikiToken();
    if (!token) {
      setCatalogRenameError('登录状态已失效，请重新登录。');
      return;
    }
    setCatalogRenameBusy(true);
    setCatalogRenameError('');
    try {
      const response = await fetch(`/api/games/${encodeURIComponent(gameKey)}/catalogs`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json', 'access-token': token },
        body: JSON.stringify({ catalogKey: catalog.catalogKey, renameOnly: true, name }),
      });
      const payload = (await response.json().catch(() => null)) as { catalogKey?: string; name?: string; error?: string } | null;
      if (!response.ok || !payload?.name) throw new Error(payload?.error || `图鉴名称保存失败（HTTP ${response.status}）`);
      const renamed = payload.name;
      updateAdditionalCatalog(catalog.catalogKey, (current) => ({ ...current, name: renamed }));
      const saved = savedCatalogsRef.current[catalog.catalogKey];
      if (saved) savedCatalogsRef.current[catalog.catalogKey] = { ...saved, name: renamed };
      const navigation = (window as Window & { __GAME_WIKI_CATALOGS__?: Array<{ catalogKey: string; name: string }> }).__GAME_WIKI_CATALOGS__;
      const navigationItem = navigation?.find((item) => item.catalogKey === catalog.catalogKey);
      if (navigationItem) navigationItem.name = renamed;
      document.querySelectorAll<HTMLElement>('.collection-entries [data-collection]').forEach((entry) => {
        if (entry.dataset.collection === catalog.catalogKey) {
          const title = entry.querySelector('.collection-copy strong');
          if (title) title.textContent = renamed;
        }
      });
      window.dispatchEvent(new CustomEvent('game-wiki-catalog-name-change', {
        detail: { catalogKey: catalog.catalogKey, name: renamed },
      }));
      setAdditionalCatalogMessage({ catalogKey: catalog.catalogKey, text: '图鉴名称已保存。' });
      setRenamingCatalogKey('');
    } catch (reason) {
      setCatalogRenameError(reason instanceof Error ? reason.message : '图鉴名称保存失败');
    } finally {
      setCatalogRenameBusy(false);
    }
  };
  const saveCatalogShape = async (catalog: ManagedCatalog, listShape: NonNullable<CatalogTemplateSettings['listShape']>) => {
    const token = readWikiToken();
    if (!token) {
      setAdditionalCatalogMessage({ catalogKey: catalog.catalogKey, text: '登录状态已失效，请重新登录。' });
      return;
    }
    const previousShape = savedCatalogsRef.current[catalog.catalogKey]?.settings.listShape || 'square';
    setAdditionalCatalogSaving(catalog.catalogKey);
    setAdditionalCatalogMessage({ catalogKey: catalog.catalogKey, text: '' });
    try {
      const isDedicatedHero = gameKey === 'arcanatactics' && catalog.catalogKey === 'hero';
      const response = await fetch(isDedicatedHero
        ? `/api/games/${encodeURIComponent(gameKey)}/catalog-settings`
        : `/api/games/${encodeURIComponent(gameKey)}/catalogs`, {
        method: isDedicatedHero ? 'PUT' : 'PATCH',
        headers: { 'content-type': 'application/json', 'access-token': token },
        body: JSON.stringify(isDedicatedHero
          ? { catalogType: 'hero', settings: { ...catalog.settings, listShape } }
          : { catalogKey: catalog.catalogKey, shapeOnly: true, listShape }),
      });
      const payload = await response.json() as { error?: string };
      if (!response.ok) throw new Error(payload.error || '卡片形状保存失败');
      const saved = savedCatalogsRef.current[catalog.catalogKey];
      if (saved) savedCatalogsRef.current[catalog.catalogKey] = { ...saved, settings: { ...saved.settings, listShape } };
      if (isDedicatedHero) setPersistedTemplateSettings((current) => ({ ...current, listShape }));
      window.dispatchEvent(new CustomEvent('game-wiki-catalog-shape-change', {
        detail: { catalogKey: catalog.catalogKey, listShape },
      }));
      setAdditionalCatalogMessage({ catalogKey: catalog.catalogKey, text: '列表卡片形状已保存。' });
    } catch (reason) {
      updateAdditionalCatalog(catalog.catalogKey, (current) => ({ ...current, settings: { ...current.settings, listShape: previousShape } }));
      if (gameKey === 'arcanatactics' && catalog.catalogKey === 'hero') {
        setTemplateSettings((current) => ({ ...current, listShape: previousShape }));
      }
      setAdditionalCatalogMessage({ catalogKey: catalog.catalogKey, text: reason instanceof Error ? reason.message : '卡片形状保存失败' });
    } finally {
      setAdditionalCatalogSaving('');
    }
  };
  const saveAdditionalCatalog = async (catalog: ManagedCatalog) => {
    const report = (text: string) => setAdditionalCatalogMessage({ catalogKey: catalog.catalogKey, text });
    const token = readWikiToken();
    if (!token) {
      report('登录状态已失效，请重新登录。');
      return;
    }
    setAdditionalCatalogSaving(catalog.catalogKey);
    report('');
    try {
      const mapping = catalog.settings.sourceMapping || {};
      const saved = savedCatalogsRef.current[catalog.catalogKey];
      const sourceChanged = !saved || saved.sourceUrl.trim() !== catalog.sourceUrl.trim() || JSON.stringify(saved.settings.sourceMapping || {}) !== JSON.stringify(mapping);
      let validationTaskId: string | undefined;
      if (sourceChanged) {
      if (!catalog.sourceUrl.trim()) throw new Error('请先填写图鉴列表 JSON 地址');
      const validationResponse = await fetch(`/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(catalog.catalogKey)}/mapping-validations`, {
        method: 'POST', headers: { 'content-type': 'application/json', 'access-token': token },
        body: JSON.stringify({ sourceUrl: catalog.sourceUrl.trim(), mapping }),
      });
      const validation = await validationResponse.json() as { taskId?: string; error?: string };
      if (!validationResponse.ok || !validation.taskId) throw new Error(validation.error || '无法创建字段映射校验任务');
      let validated = false;
      for (let attempt = 0; attempt < 45; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const progressResponse = await fetch(`/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(catalog.catalogKey)}/update-tasks/${encodeURIComponent(validation.taskId)}`, { headers: { 'access-token': token }, cache: 'no-store' });
        const progress = await progressResponse.json() as { status?: string; errorMessage?: string; error?: string };
        if (!progressResponse.ok) throw new Error(progress.error || '字段映射校验状态读取失败');
        if (progress.status === 'failed') throw new Error(progress.errorMessage || '真实 JSON 校验失败');
        if (progress.status === 'validated') { validated = true; break; }
      }
      if (!validated) throw new Error('等待字段映射校验超时，请检查 Agent 是否运行');
      validationTaskId = validation.taskId;
      }
      const response = await fetch(
        `/api/games/${encodeURIComponent(gameKey)}/catalogs`,
        {
          method: 'PATCH',
          headers: {
            'content-type': 'application/json',
            'access-token': token,
          },
          body: JSON.stringify({ ...catalog, settings: { ...catalog.settings, sourceMapping: mapping }, validationTaskId }),
        },
      );
      const payload = (await response.json()) as {
        catalog?: ManagedCatalog;
        error?: string;
      };
      if (!response.ok) throw new Error(payload.error || '图鉴设置保存失败');
      if (payload.catalog) {
        savedCatalogsRef.current[catalog.catalogKey] = payload.catalog;
        updateAdditionalCatalog(catalog.catalogKey, () => payload.catalog!);
      }
      report(`${catalog.name}设置已保存。`);
    } catch (reason) {
      const saved = savedCatalogsRef.current[catalog.catalogKey];
      if (saved) updateAdditionalCatalog(catalog.catalogKey, (current) => ({
        ...current,
        settings: { ...current.settings, updatePolicy: saved.settings.updatePolicy },
      }));
      report(
        reason instanceof Error ? reason.message : '图鉴设置保存失败',
      );
    } finally {
      setAdditionalCatalogSaving('');
    }
  };
  const deleteCatalog = async () => {
    if (!catalogDeleteTarget) return;
    const token = readWikiToken();
    if (!token) {
      setCatalogDeleteError('登录状态已失效，请重新登录。');
      return;
    }
    setCatalogDeleteBusy(true);
    setCatalogDeleteError('');
    try {
      const response = await fetch(
        `/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(catalogDeleteTarget.catalogKey)}`,
        { method: 'DELETE', headers: { 'access-token': token } },
      );
      const payload = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) throw new Error(payload.error || '删除图鉴失败');
      setCatalogs((current) => current.filter((catalog) => catalog.catalogKey !== catalogDeleteTarget.catalogKey));
      if (catalogDeleteTarget.catalogKey === 'hero') setHeroCatalogPresent(false);
      setCatalogDeleteTarget(null);
    } catch (reason) {
      setCatalogDeleteError(reason instanceof Error ? reason.message : '删除图鉴失败');
    } finally {
      setCatalogDeleteBusy(false);
    }
  };
  const checkCatalogUpdates = async (catalog: ManagedCatalog) => {
    setCheckingCatalogKey(catalog.catalogKey);
    try {
      const url = new URL(catalog.sourceUrl);
      const isArcanaCards = isLegacyCardCatalog(gameKey, catalog.catalogKey);
      if (isArcanaCards) {
        const response = await fetch(`/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(catalog.catalogKey === 'catalog_65fcfc5c37224d95987a03711aec9e67' ? 'kapai' : catalog.catalogKey)}/import-preview`, {
          method: 'POST', headers: { 'content-type': 'application/json', 'access-token': readWikiToken() || '' },
          body: JSON.stringify({ sourceUrl: url.href }),
        });
        const payload = await response.json() as { digest: string; items: unknown[]; error?: string };
        if (!response.ok) throw new Error(payload.error || `检查失败（${response.status}）`);
        setCardImportError('');
        setCardImport({ catalog: { ...catalog, sourceUrl: url.href }, digest: payload.digest, count: payload.items.length });
        return;
      }
      const token = readWikiToken();
      if (!token) throw new Error('登录状态已失效，请重新登录。');
      const genericResponse = await fetch(`/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(catalog.catalogKey)}/update-tasks`, {
        method: 'POST', headers: { 'content-type': 'application/json', 'access-token': token },
        body: JSON.stringify({ sourceUrl: catalog.sourceUrl.trim() }),
      });
      const genericPayload = await genericResponse.json() as { task?: { taskId: string }; error?: string };
      if (!genericResponse.ok) throw new Error(genericPayload.error || `检查失败（${genericResponse.status}）`);
      if (!genericPayload.task?.taskId) throw new Error('任务创建结果缺少 ID');
      setGenericTask({ catalog, task: { taskId: genericPayload.task.taskId, status: 'queued', result: [], selectedIds: [] } });
      setGenericTaskSelected([]);
      setGenericError('');
      return;
    } catch (error) {
      setCatalogCheckMessage((current) => ({ ...current, [catalog.catalogKey]: error instanceof Error ? error.message : '检查失败' }));
    } finally {
      setCheckingCatalogKey('');
    }
  };
  const confirmCardImport = async () => {
    if (!cardImport || cardImportBusy) return;
    setCardImportBusy(true);
    setCardImportError('');
    try {
      const { catalog, digest } = cardImport;
      const apiCatalog = gameKey === 'arcanatactics' && catalog.catalogKey === 'catalog_65fcfc5c37224d95987a03711aec9e67' ? 'kapai' : catalog.catalogKey;
      const response = await fetch(`/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(apiCatalog)}/import`, {
        method: 'POST', headers: { 'content-type': 'application/json', 'access-token': readWikiToken() || '' },
        body: JSON.stringify({ sourceUrl: catalog.sourceUrl, digest, confirm: true }),
      });
      const payload = await response.json() as { items?: unknown[]; error?: string };
      if (!response.ok) throw new Error(payload.error || `入库失败（${response.status}）`);
      updateAdditionalCatalog(catalog.catalogKey, () => catalog);
      setCatalogCheckMessage(current => ({ ...current, [catalog.catalogKey]: `卡牌详情已入库，共 ${payload.items?.length || 0} 条。刷新图鉴页后使用数据库 ID 读取。` }));
      setCardImport(null);
    } catch (error) {
      setCardImportError(error instanceof Error ? error.message : '入库失败');
    } finally { setCardImportBusy(false); }
  };
  const genericEndpoint = (catalog: ManagedCatalog, action: string) =>
    `/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(catalog.catalogKey)}/generic/${action}`;
  const openLatestGenericTask = async (catalog: ManagedCatalog) => {
    setGenericError('');
    try {
      const base = `/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(catalog.catalogKey)}/update-tasks`;
      const headers = { 'access-token': readWikiToken() || '' };
      const response = await fetch(base, { headers, cache: 'no-store' });
      const payload = await response.json() as { items?: Array<{ taskId: string; status: string }>; error?: string };
      if (!response.ok) throw new Error(payload.error || `读取任务失败（${response.status}）`);
      const latest = payload.items?.find((item) => ['awaiting_confirmation', 'queued', 'processing', 'upload_queued', 'uploading'].includes(item.status)) || payload.items?.[0];
      if (!latest) throw new Error('此图鉴暂无更新任务');
      const detailResponse = await fetch(`${base}/${encodeURIComponent(latest.taskId)}`, { headers, cache: 'no-store' });
      const detail = await detailResponse.json() as GenericTask & { error?: string };
      if (!detailResponse.ok) throw new Error(detail.error || `读取任务失败（${detailResponse.status}）`);
      setGenericTask({ catalog, task: detail });
      setGenericTaskSelected([]);
    } catch (reason) { setCatalogCheckMessage((current) => ({ ...current, [catalog.catalogKey]: reason instanceof Error ? reason.message : '读取任务失败' })); }
  };
  useEffect(() => {
    if (!genericTask || !['queued', 'processing', 'upload_queued', 'uploading'].includes(genericTask.task.status)) return;
    const endpoint = `/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(genericTask.catalog.catalogKey)}/update-tasks/${encodeURIComponent(genericTask.task.taskId)}`;
    const timer = window.setInterval(async () => {
      try {
        const response = await fetch(endpoint, { headers: { 'access-token': readWikiToken() || '' }, cache: 'no-store' });
        const payload = await response.json() as GenericTask & { error?: string };
        if (!response.ok) throw new Error(payload.error || `读取任务失败（${response.status}）`);
        setGenericTask((current) => current?.task.taskId === payload.taskId ? { ...current, task: payload } : current);
      } catch (reason) { setGenericError(reason instanceof Error ? reason.message : '读取任务失败'); }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [gameKey, genericTask?.catalog.catalogKey, genericTask?.task.taskId, genericTask?.task.status]);
  const confirmGenericTask = async () => {
    if (!genericTask || genericTaskSelected.length === 0) return;
    setGenericBusy(true);
    setGenericError('');
    try {
      const response = await fetch(`/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(genericTask.catalog.catalogKey)}/update-tasks/${encodeURIComponent(genericTask.task.taskId)}/confirm`, {
        method: 'POST', headers: { 'content-type': 'application/json', 'access-token': readWikiToken() || '' },
        body: JSON.stringify({ ids: genericTaskSelected }),
      });
      const payload = await response.json() as { error?: string };
      if (!response.ok) throw new Error(payload.error || `确认失败（${response.status}）`);
      setGenericTask((current) => current ? { ...current, task: { ...current.task, status: 'upload_queued' } } : current);
    } catch (reason) { setGenericError(reason instanceof Error ? reason.message : '确认失败'); }
    finally { setGenericBusy(false); }
  };
  const applyGeneric = async () => {
    if (!genericCheck || !genericSelected.length) return;
    setGenericBusy(true);
    setGenericError('');
    try {
      const response = await fetch(genericEndpoint(genericCheck.catalog, 'apply'), {
        method: 'POST', headers: { 'content-type': 'application/json', 'access-token': readWikiToken() || '' },
        body: JSON.stringify({ sourceUrl: genericCheck.result.sourceUrl, digest: genericCheck.result.digest, ids: genericSelected }),
      });
      const payload = await response.json() as { updated?: number; error?: string };
      if (!response.ok) throw new Error(payload.error || `更新失败（${response.status}）`);
      setCatalogCheckMessage((current) => ({ ...current, [genericCheck.catalog.catalogKey]: `已更新 ${payload.updated || 0} 条，可在更新记录中回滚。` }));
      setGenericCheck(null);
    } catch (reason) { setGenericError(reason instanceof Error ? reason.message : '更新失败'); }
    finally { setGenericBusy(false); }
  };
  const loadGenericHistory = async (catalog: ManagedCatalog) => {
    try {
      const response = await fetch(genericEndpoint(catalog, 'history'), { headers: { 'access-token': readWikiToken() || '' }, cache: 'no-store' });
      const payload = await response.json() as { items?: GenericBatch[]; error?: string };
      if (!response.ok) throw new Error(payload.error || `读取失败（${response.status}）`);
      setGenericHistory({ catalog, items: payload.items || [] });
      setGenericError('');
    } catch (reason) { setCatalogCheckMessage((current) => ({ ...current, [catalog.catalogKey]: reason instanceof Error ? reason.message : '读取失败' })); }
  };
  const rollbackGeneric = async (batchId: string) => {
    if (!genericHistory) return;
    setGenericBusy(true);
    setGenericError('');
    try {
      const response = await fetch(genericEndpoint(genericHistory.catalog, 'rollback'), {
        method: 'POST', headers: { 'content-type': 'application/json', 'access-token': readWikiToken() || '' }, body: JSON.stringify({ batchId }),
      });
      const payload = await response.json() as { error?: string };
      if (!response.ok) throw new Error(payload.error || `回滚失败（${response.status}）`);
      await loadGenericHistory(genericHistory.catalog);
    } catch (reason) { setGenericError(reason instanceof Error ? reason.message : '回滚失败'); }
    finally { setGenericBusy(false); }
  };
  const saveAsSharedTemplate = async () => {
    if (!templateModalKey || !templateModalSettings) return;
    if (templateModalSettings.updatePolicy !== 'custom') {
      setSharedTemplateError('请先选择“独立维护一套代码”，再保存为通用模板。');
      return;
    }
    const token = readWikiToken();
    if (!token) {
      setSharedTemplateError('登录状态已失效，请重新登录。');
      return;
    }
    if (!sharedTemplateName.trim()) {
      setSharedTemplateError('请输入通用模板名称。');
      return;
    }
    setSharedTemplateSaving(true);
    setSharedTemplateError('');
    try {
      const response = await fetch('/api/catalog-templates', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'access-token': token,
        },
        body: JSON.stringify({
          gameKey,
          name: sharedTemplateName.trim(),
          catalogKind: 'hero',
          sourceCatalogKey: templateModalKey,
          sourceTemplateId: templateModalSettings.detailTemplate,
        }),
      });
      const payload = (await response.json()) as {
        template?: SharedCatalogTemplate;
        error?: string;
      };
      if (!response.ok) throw new Error(payload.error || '保存通用模板失败');
      if (payload.template)
        setSharedTemplates((current) => [...current, payload.template!]);
      setSharedTemplateName('');
      setSaveSharedOpen(false);
      setSharedTemplateError('已保存为新的通用模板。');
    } catch (reason) {
      setSharedTemplateError(
        reason instanceof Error ? reason.message : '保存通用模板失败',
      );
    } finally {
      setSharedTemplateSaving(false);
    }
  };
  const saveTemplateSettings = async () => {
    const token = readWikiToken();
    if (!token) {
      setTemplateMessage('登录状态已失效，请重新登录。');
      return;
    }
    setTemplateSaving(true);
    setTemplateMessage('');
    try {
      const response = await fetch(
        `/api/games/${encodeURIComponent(gameKey)}/catalog-settings`,
        {
          method: 'PUT',
          headers: {
            'content-type': 'application/json',
            'access-token': token,
          },
          body: JSON.stringify({
            catalogType: 'hero',
            settings: templateSettings,
          }),
        },
      );
      const payload = (await response.json()) as {
        settings?: CatalogTemplateSettings;
        error?: string;
      };
      if (!response.ok) throw new Error(payload.error || '模板设置保存失败');
      if (payload.settings) {
        setTemplateSettings(payload.settings);
        setPersistedTemplateSettings(payload.settings);
      }
      setTemplateMessage('图鉴模板设置已保存。');
    } catch (reason) {
      setTemplateMessage(
        reason instanceof Error ? reason.message : '模板设置保存失败',
      );
    } finally {
      setTemplateSaving(false);
    }
  };
  const visibleChanges = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase('zh-CN');
    const items = result
      ? filter === 'changed'
        ? result.changes
        : filter === 'unchanged'
          ? result.unchangedHeroes || []
          : filter === 'all'
            ? [...result.changes, ...(result.unchangedHeroes || [])]
            : result.changes.filter((item) => item.type === filter)
      : [];
    return (
      items.filter(
        (item) =>
          !query ||
          item.name.toLocaleLowerCase('zh-CN').includes(query) ||
          item.id.toLocaleLowerCase('zh-CN').includes(query),
      ) ?? []
    );
  }, [filter, result, searchQuery]);
  const displayedChanges = visibleChanges.slice(0, 30);
  const selectableDisplayed = displayedChanges.filter(
    (item) => item.type !== 'unchanged',
  );
  const selectedChanges = result
    ? result.changes.filter((item) => selectedIds.includes(item.id))
    : [];
  const allDisplayedSelected =
    selectableDisplayed.length > 0 &&
    selectableDisplayed.every((item) => selectedIds.includes(item.id));
  const selectedRequest: SyncRequest = {
    heroIds: selectedChanges
      .filter((item) => item.type !== 'removed')
      .map((item) => item.id),
    archiveIds: selectedChanges
      .filter((item) => item.type === 'removed')
      .map((item) => item.id),
  };
  const retryCandidate =
    retryRequest ||
    (selectedRequest.heroIds.length + selectedRequest.archiveIds.length
      ? selectedRequest
      : null);

  useEffect(() => {
    if (!preview) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !syncing) setPreview(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [preview, syncing]);

  const checkUpdates = async () => {
    const token = readWikiToken();
    if (!token) {
      setError('登录状态已失效，请重新登录。');
      return;
    }
    setChecking(true);
    setError('');
    try {
      const response = await fetch(
        `/api/catalog/${encodeURIComponent(gameKey)}/check`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'access-token': token,
          },
          body: JSON.stringify({ sourceUrl }),
        },
      );
      const payload = (await response.json().catch(() => ({}))) as
        | CheckResult
        | { error?: string };
      if (!response.ok || !('summary' in payload)) {
        throw new Error(
          'error' in payload && payload.error ? payload.error : '检查失败',
        );
      }
      setResult(payload);
      setPreview(null);
      setFilter('changed');
      setSearchQuery('');
      setSelectedIds([]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '检查失败');
    } finally {
      setChecking(false);
    }
  };

  const loadHistory = async () => {
    const token = readWikiToken();
    if (!token) return;
    setHistoryLoading(true);
    try {
      const response = await fetch(
        `/api/catalog/${encodeURIComponent(gameKey)}/history`,
        { headers: { 'access-token': token }, cache: 'no-store' },
      );
      const payload = (await response.json().catch(() => ({}))) as {
        batches?: SyncBatch[];
        error?: string;
      };
      if (!response.ok) throw new Error(payload.error || '更新记录读取失败');
      setHistory(payload.batches || []);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '更新记录读取失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) void loadHistory();
  };

  const submitSync = async (heroIds: string[], archiveIds: string[]) => {
    const token = readWikiToken();
    if (!token) return setError('登录状态已失效，请重新登录。');
    if (!heroIds.length && !archiveIds.length) return;
    const request = {
      heroIds: [...heroIds],
      archiveIds: [...archiveIds],
    } satisfies SyncRequest;
    setSyncing(true);
    setError('');
    setSyncMessage('');
    const batchId = createBatchId();
    let latestProgress: SyncProgress = {
      status: 'queued',
      stage: 'queued',
      percent: 1,
      current: 0,
      total: heroIds.length + archiveIds.length,
      message: '正在连接资料站并创建更新任务',
    };
    setSyncProgress(latestProgress);
    let remainingRequest: SyncRequest | null = null;
    let stopped = false;
    let polling = false;
    const pollProgress = async () => {
      if (stopped || polling) return;
      polling = true;
      try {
        const response = await fetch(
          `/api/catalog/${encodeURIComponent(gameKey)}/progress?batchId=${encodeURIComponent(batchId)}`,
          {
            headers: { 'access-token': token },
            cache: 'no-store',
          },
        );
        if (response.status === 404) return;
        const payload = (await response.json().catch(() => ({}))) as
          | SyncProgress
          | { error?: string };
        if (response.ok && 'percent' in payload) {
          latestProgress = payload;
          setSyncProgress(payload);
          if (
            payload.status === 'failed' &&
            (payload.remainingHeroIds?.length ||
              payload.remainingArchiveIds?.length)
          ) {
            setRetryRequest({
              heroIds: payload.remainingHeroIds || [],
              archiveIds: payload.remainingArchiveIds || [],
            });
          }
        }
      } finally {
        polling = false;
      }
    };
    const progressTimer = window.setInterval(() => void pollProgress(), 700);
    void pollProgress();
    try {
      const response = await fetch(
        `/api/catalog/${encodeURIComponent(gameKey)}/sync`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'access-token': token,
          },
          body: JSON.stringify({ batchId, sourceUrl, heroIds, archiveIds }),
        },
      );
      const payload = (await response.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
        remainingHeroIds?: string[];
        remainingArchiveIds?: string[];
      };
      if (!response.ok) {
        if (
          payload.remainingHeroIds?.length ||
          payload.remainingArchiveIds?.length
        ) {
          remainingRequest = {
            heroIds: payload.remainingHeroIds || [],
            archiveIds: payload.remainingArchiveIds || [],
          };
          setRetryRequest(remainingRequest);
        }
        throw new Error(payload.error || '图鉴更新失败');
      }
      setSyncMessage(payload.message || '图鉴更新完成');
      setSyncProgress({
        status: 'applied',
        stage: 'complete',
        percent: 100,
        current: heroIds.length + archiveIds.length,
        total: heroIds.length + archiveIds.length,
        message: payload.message || '图鉴更新完成',
      });
      setPreview(null);
      setSelectedIds([]);
      setRetryRequest(null);
      await Promise.all([checkUpdates(), loadHistory()]);
    } catch (reason) {
      const errorMessage =
        reason instanceof Error ? reason.message : '图鉴更新失败';
      setError(errorMessage);
      setSyncProgress({
        status: 'failed',
        stage: 'failed',
        percent: latestProgress.percent,
        current: latestProgress.current,
        total: latestProgress.total,
        message: errorMessage,
      });
      setRetryRequest(
        remainingRequest ||
          (latestProgress.remainingHeroIds?.length ||
          latestProgress.remainingArchiveIds?.length
            ? {
                heroIds: latestProgress.remainingHeroIds || [],
                archiveIds: latestProgress.remainingArchiveIds || [],
              }
            : request),
      );
    } finally {
      stopped = true;
      window.clearInterval(progressTimer);
      setSyncing(false);
    }
  };

  const confirmSync = () => {
    if (!result) return;
    const heroIds = result.changes
      .filter((item) => item.type !== 'removed')
      .map((item) => item.id);
    const archiveIds = result.changes
      .filter((item) => item.type === 'removed')
      .map((item) => item.id);
    void submitSync(heroIds, archiveIds);
  };

  const syncSelected = () => {
    void submitSync(selectedRequest.heroIds, selectedRequest.archiveIds);
  };

  const forceRefreshHero = (item: CatalogChange) => {
    if (item.type === 'removed' || syncing) return;
    if (
      !window.confirm(
        `确定强制刷新「${item.name}」（ID ${item.id}）吗？这会重新读取资料站 JSON 并生成一个新版本。`,
      )
    )
      return;
    void submitSync([item.id], []);
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  };

  const toggleDisplayed = () => {
    const displayedIds = selectableDisplayed.map((item) => item.id);
    setSelectedIds((current) =>
      allDisplayedSelected
        ? current.filter((id) => !displayedIds.includes(id))
        : [...new Set([...current, ...displayedIds])],
    );
  };

  const rollbackBatch = async (batchId: string) => {
    if (!window.confirm('确定回滚这次图鉴更新吗？当前展示内容会恢复到更新前。'))
      return;
    const token = readWikiToken();
    if (!token) return setError('登录状态已失效，请重新登录。');
    setRollingBackId(batchId);
    setError('');
    try {
      const response = await fetch(
        `/api/catalog/${encodeURIComponent(gameKey)}/rollback`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'access-token': token,
          },
          body: JSON.stringify({ batchId }),
        },
      );
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      if (!response.ok) throw new Error(payload.error || '回滚失败');
      setSyncMessage('已恢复到本次更新前的图鉴内容。');
      await Promise.all([checkUpdates(), loadHistory()]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '回滚失败');
    } finally {
      setRollingBackId('');
    }
  };

  const previewHero = async (heroId: string) => {
    const token = readWikiToken();
    if (!token) {
      setError('登录状态已失效，请重新登录。');
      return;
    }
    setPreviewingId(heroId);
    setError('');
    try {
      const response = await fetch(
        `/api/catalog/${encodeURIComponent(gameKey)}/preview`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'access-token': token,
          },
          body: JSON.stringify({ sourceUrl, heroId }),
        },
      );
      const payload = (await response.json().catch(() => ({}))) as
        | CatalogPreview
        | { error?: string };
      if (!response.ok || !('hero' in payload)) {
        throw new Error(
          'error' in payload && payload.error ? payload.error : '转换预览失败',
        );
      }
      setPreview(payload);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '转换预览失败');
    } finally {
      setPreviewingId('');
    }
  };

  const templateModalCatalog = templateModalKey
    ? catalogs.find((catalog) => catalog.catalogKey === templateModalKey)
    : undefined;
  const templateModalSettings =
    templateModalKey === 'hero'
      ? templateSettings
      : templateModalCatalog?.settings;
  const isTemplateForkLocked =
    templateModalKey === 'hero'
      ? persistedTemplateSettings.updatePolicy === 'custom'
      : savedCatalogsRef.current[templateModalKey || '']?.settings.updatePolicy === 'custom';

  return (
    <section className="catalog-admin-panel">
      <div className="catalog-admin-heading">
        <div>
          <span>图鉴管理</span>
          <small>分别管理每一种图鉴的数据源与详情模板。</small>
        </div>
        <button type="button" onClick={openAddCatalog}>
          <Plus />
          新增图鉴
        </button>
      </div>
      <div className="catalog-admin-toolbar">
        <div>
          <span>图鉴数据源</span>
          <strong>{catalogs.length} 个图鉴</strong>
        </div>
        <button type="button" onClick={checkUpdates} disabled={checking}>
          <RefreshCw className={checking ? 'is-spinning' : ''} />
          {checking ? '正在检查' : '检查更新所有'}
        </button>
      </div>

      {heroCatalogPresent && <article className="catalog-source-card">
        <header>
          <div className="catalog-source-title">
            <CatalogIconUpload gameKey={gameKey} catalog={catalogs.find((catalog) => catalog.catalogKey === 'hero') || { catalogKey: 'hero', name: '英雄图鉴', displayMode: 'standalone', sourceUrl, adapterKey: 'arcanatactics-v1', settings: persistedTemplateSettings }} onSaved={(iconUrl) => updateAdditionalCatalog('hero', (catalog) => ({ ...catalog, iconUrl }))} />
            <div>
              <h3>英雄图鉴</h3>
              <p>阿尔克纳战术 · 清单索引检查</p>
            </div>
          </div>
          <div className="catalog-card-actions">
            <div className="catalog-template-button-wrap">
              <button
                className="catalog-history-button"
                type="button"
                onClick={() => setTemplateModalKey('hero')}
              >
                模板设置
              </button>
              {persistedTemplateSettings.updatePolicy === 'custom' && (
                <span>独立</span>
              )}
            </div>
            <button
              className="catalog-history-button"
              type="button"
              onClick={toggleHistory}
            >
              <History />
              更新记录
            </button>
            <button
              className="catalog-history-button catalog-delete-button"
              type="button"
              onClick={() => {
                setCatalogDeleteError('');
                setCatalogDeleteTarget({
                  catalogKey: 'hero',
                  displayMode: 'standalone',
                  name: '英雄图鉴',
                  sourceUrl,
                  adapterKey: 'arcanatactics-v1',
                  settings: persistedTemplateSettings,
                });
              }}
            >
              <Trash2 />
              删除图鉴
            </button>
          </div>
        </header>

        <label className="catalog-source-field">
          <span>英雄列表 JSON 地址</span>
          <input
            type="url"
            value={sourceUrl}
            onChange={(event) => setSourceUrl(event.target.value)}
            spellCheck={false}
          />
          <button
            type="button"
            onClick={checkUpdates}
            disabled={checking || !sourceUrl.trim()}
          >
            {checking ? (
              <LoaderCircle className="is-spinning" />
            ) : (
              <RefreshCw />
            )}
            {checking ? '检查中' : '检查更新'}
          </button>
        </label>

        {error && (
          <p className="catalog-check-error" role="alert">
            <AlertCircle />
            {error}
          </p>
        )}

        {historyOpen && (
          <div className="catalog-check-history">
            <strong>已提交的更新记录</strong>
            {historyLoading ? (
              <p>正在读取更新记录…</p>
            ) : history.length ? (
              history.map((item) => (
                <div key={item.batchId}>
                  <Clock3 />
                  <span>
                    {new Date(item.createdAt).toLocaleString('zh-CN')}
                  </span>
                  <small>
                    新增 {item.added} · 更新 {item.updated} · 归档{' '}
                    {item.archived}
                    {item.status === 'rolled_back' ? ' · 已回滚' : ''}
                    {item.status === 'failed' ? ' · 部分完成' : ''}
                  </small>
                  {(item.status === 'applied' ||
                    (item.status === 'failed' &&
                      item.added + item.updated + item.archived > 0)) && (
                    <button
                      type="button"
                      disabled={Boolean(rollingBackId)}
                      onClick={() => rollbackBatch(item.batchId)}
                    >
                      {rollingBackId === item.batchId ? '回滚中' : '回滚'}
                    </button>
                  )}
                </div>
              ))
            ) : (
              <p>还没有提交过图鉴更新。</p>
            )}
          </div>
        )}

        {!result && !checking && !error && (
          <div className="catalog-check-empty">
            <RefreshCw />
            <strong>等待检查资料源</strong>
            <p>
              检查会先核对清单，仅对哈希变化的英雄读取详情，不会修改现有数据。
            </p>
          </div>
        )}

        {checking && (
          <div className="catalog-check-empty is-loading" aria-live="polite">
            <LoaderCircle className="is-spinning" />
            <strong>正在读取英雄列表</strong>
            <p>正在核对清单哈希与当前使用版本，必要时复核英雄详情。</p>
          </div>
        )}

        {result && !checking && (
          <div className="catalog-check-result">
            <div className="catalog-result-meta">
              <span>
                <CheckCircle2 />
                检查完成
              </span>
              <small>
                上游 {result.sourceCount} 位 · 本站 {result.currentCount} 位 ·
                清单版本 {result.schemaVersion}
              </small>
              <time>{new Date(result.checkedAt).toLocaleString('zh-CN')}</time>
            </div>
            <div className="catalog-result-summary">
              {(
                [
                  ['changed', '全部变化', result.changes.length],
                  ['added', '新增', result.summary.added],
                  ['updated', '有更新', result.summary.updated],
                  ['removed', '上游移除', result.summary.removed],
                ] as const
              ).map(([id, label, count]) => (
                <button
                  type="button"
                  className={filter === id ? 'is-active' : ''}
                  onClick={() => setFilter(id)}
                  key={id}
                >
                  <span>{label}</span>
                  <b>{count}</b>
                </button>
              ))}
              <button
                type="button"
                className={filter === 'unchanged' ? 'is-active' : ''}
                onClick={() => setFilter('unchanged')}
              >
                <span>无变化</span>
                <b>{result.summary.unchanged}</b>
              </button>
            </div>

            <div className="catalog-change-tools">
              <label className="catalog-change-search">
                <Search aria-hidden="true" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="搜索英雄名称或 ID"
                  aria-label="搜索英雄名称或 ID"
                />
              </label>
              <label className="catalog-change-filter">
                <span>状态</span>
                <select
                  value={filter}
                  onChange={(event) =>
                    setFilter(event.target.value as CatalogFilter)
                  }
                  aria-label="筛选更新状态"
                >
                  <option value="all">全部状态</option>
                  <option value="changed">全部变化</option>
                  <option value="added">新增</option>
                  <option value="updated">有更新</option>
                  <option value="removed">上游移除</option>
                  <option value="unchanged">无变化</option>
                </select>
              </label>
              <small>
                显示 {visibleChanges.length} /{' '}
                {result.changes.length + (result.unchangedHeroes?.length || 0)}{' '}
                项
              </small>
            </div>

            <div className="catalog-selection-actions">
              <label>
                <input
                  type="checkbox"
                  checked={allDisplayedSelected}
                  disabled={!selectableDisplayed.length || syncing}
                  onChange={toggleDisplayed}
                />
                选择当前显示
              </label>
              <span>已选 {selectedChanges.length} 项</span>
              {selectedChanges.length > 0 && (
                <button
                  type="button"
                  className="catalog-selection-clear"
                  disabled={syncing}
                  onClick={() => setSelectedIds([])}
                >
                  清空选择
                </button>
              )}
              <button
                type="button"
                className="catalog-selection-submit"
                disabled={syncing || selectedChanges.length === 0}
                onClick={syncSelected}
              >
                {syncing ? '正在更新' : `更新已选（${selectedChanges.length}）`}
              </button>
            </div>

            {syncProgress && (
              <div
                className="catalog-sync-progress"
                data-status={syncProgress.status}
                aria-live="polite"
              >
                <div>
                  <span>
                    {syncing && <LoaderCircle className="is-spinning" />}
                    {syncProgress.message}
                  </span>
                  <strong>{syncProgress.percent}%</strong>
                </div>
                <progress max="100" value={syncProgress.percent}>
                  {syncProgress.percent}%
                </progress>
                {syncProgress.total > 0 &&
                  syncProgress.stage !== 'complete' && (
                    <small>
                      已完成 {syncProgress.current} / {syncProgress.total}
                    </small>
                  )}
                {syncProgress.status === 'failed' && retryCandidate && (
                  <button
                    type="button"
                    className="catalog-sync-retry"
                    disabled={syncing}
                    onClick={() =>
                      void submitSync(
                        retryCandidate.heroIds,
                        retryCandidate.archiveIds,
                      )
                    }
                  >
                    <RefreshCw />
                    重试本批（
                    {retryCandidate.heroIds.length +
                      retryCandidate.archiveIds.length}
                    ）
                  </button>
                )}
              </div>
            )}

            <div className="catalog-change-list">
              <div className="catalog-change-head">
                <span>选择</span>
                <span>英雄</span>
                <span>状态</span>
                <span>本站版本</span>
                <span>资料站版本</span>
                <span>操作</span>
              </div>
              {displayedChanges.map((item) => (
                <div
                  className="catalog-change-row"
                  key={`${item.type}-${item.id}`}
                >
                  <span className="catalog-change-select">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      disabled={item.type === 'unchanged' || syncing}
                      aria-label={`选择${item.name}`}
                      onChange={() => toggleSelected(item.id)}
                    />
                  </span>
                  <span>
                    <b>{item.name}</b>
                    <small>ID {item.id}</small>
                    {item.reasons?.length ? (
                      <small title={item.reasons.join('、')}>
                        {item.reasons.join(' · ')}
                      </small>
                    ) : null}
                  </span>
                  <em data-type={item.type}>{statusLabels[item.type]}</em>
                  <time>{item.currentUpdatedAt || '—'}</time>
                  <time>{item.sourceUpdatedAt || '—'}</time>
                  <span className="catalog-row-actions">
                    <button
                      type="button"
                      className="catalog-preview-button"
                      disabled={
                        item.type === 'removed' ||
                        Boolean(previewingId) ||
                        syncing
                      }
                      onClick={() => previewHero(item.id)}
                    >
                      {previewingId === item.id ? (
                        <LoaderCircle className="is-spinning" />
                      ) : (
                        <Eye />
                      )}
                      {previewingId === item.id ? '转换中' : '预览'}
                    </button>
                    <button
                      type="button"
                      className="catalog-force-refresh-button"
                      disabled={item.type === 'removed' || syncing}
                      onClick={() => forceRefreshHero(item)}
                    >
                      <RefreshCw />
                      强制刷新
                    </button>
                  </span>
                </div>
              ))}
              {!visibleChanges.length && <p>当前分类没有变化。</p>}
              {visibleChanges.length > 30 && (
                <p>当前先显示前 30 项，共 {visibleChanges.length} 项变化。</p>
              )}
            </div>

            {preview && (
              <div
                className="catalog-preview-backdrop"
                role="presentation"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget && !syncing)
                    setPreview(null);
                }}
              >
                <dialog
                  open
                  className="catalog-preview"
                  aria-modal="true"
                  aria-labelledby="catalog-preview-title"
                  aria-live="polite"
                >
                  <button
                    type="button"
                    className="catalog-preview-close"
                    aria-label="关闭预览"
                    disabled={syncing}
                    onClick={() => setPreview(null)}
                  >
                    <X />
                  </button>
                  <header>
                    <div className="catalog-preview-identity">
                      <span
                        className="catalog-preview-avatar"
                        style={
                          preview.hero.head
                            ? { backgroundImage: `url(${preview.hero.head})` }
                            : undefined
                        }
                        aria-hidden="true"
                      />
                      <div>
                        <small>转换预览 · ID {preview.hero.id}</small>
                        <h4 id="catalog-preview-title">{preview.hero.name}</h4>
                        <p>
                          {preview.hero.traits
                            .map(
                              (trait) => `${trait.label}：${trait.name || '—'}`,
                            )
                            .join(' · ')}
                        </p>
                      </div>
                    </div>
                    <div className="catalog-preview-side">
                      <div className="catalog-preview-hashes">
                        <span>源数据 {preview.sourceHash.slice(0, 18)}…</span>
                        <span>Wiki {preview.wikiHash.slice(0, 18)}…</span>
                      </div>
                      <button
                        type="button"
                        disabled={syncing}
                        onClick={() => void submitSync([preview.hero.id], [])}
                      >
                        {syncing ? '正在更新' : '强制刷新这一位'}
                      </button>
                      {error ? (
                        <small
                          className="catalog-preview-sync-error"
                          role="alert"
                        >
                          {error}
                        </small>
                      ) : syncMessage ? (
                        <output className="catalog-preview-sync-success">
                          {syncMessage}
                        </output>
                      ) : null}
                    </div>
                  </header>

                  <div className="catalog-preview-summary">
                    <span>
                      <b>{preview.summary.stats}</b> 项面板
                    </span>
                    <span>
                      <b>{preview.summary.skills}</b> 个技能
                    </span>
                    <span>
                      <b>{preview.summary.forms}</b> 个形态
                    </span>
                    <span>
                      <b>{preview.summary.quotes}</b> 条台词
                    </span>
                    <span>
                      <b>{preview.summary.resources}</b> 个资源
                    </span>
                  </div>

                  <div className="catalog-preview-columns">
                    <div>
                      <h5>转换后的技能</h5>
                      {preview.hero.skills.length ? (
                        preview.hero.skills.map((skill) => (
                          <article key={skill.id}>
                            <b>{skill.name}</b>
                            <p>{skill.ranks[0] || '资料站未提供技能说明'}</p>
                            <small>
                              升级节点：
                              {skill.upgradeLevels.join('、') || '未识别'}
                            </small>
                          </article>
                        ))
                      ) : (
                        <p className="catalog-preview-muted">
                          该英雄没有技能。
                        </p>
                      )}
                    </div>
                    <div>
                      <h5>适配器告警</h5>
                      {preview.warnings.length ? (
                        <ul>
                          {preview.warnings.map((warning) => (
                            <li key={warning}>
                              <FileWarning />
                              {warning}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="catalog-preview-muted">没有转换告警。</p>
                      )}
                    </div>
                  </div>

                  <details>
                    <summary>
                      查看资源清单（{preview.resources.length}）与忽略字段（
                      {preview.ignoredFields.length}）
                    </summary>
                    <div className="catalog-preview-details">
                      <div>
                        <b>待处理资源</b>
                        {preview.resources.slice(0, 30).map((resource) => (
                          <code key={`${resource.owner}-${resource.sourceUrl}`}>
                            [{resource.kind}] {resource.sourceUrl}
                          </code>
                        ))}
                        {preview.resources.length > 30 && (
                          <small>
                            另有 {preview.resources.length - 30} 个资源
                          </small>
                        )}
                      </div>
                      <div>
                        <b>已忽略字段</b>
                        {preview.ignoredFields.map((field) => (
                          <code key={field}>{field}</code>
                        ))}
                      </div>
                    </div>
                  </details>
                </dialog>
              </div>
            )}

            <footer className="catalog-result-actions">
              <p>
                {syncMessage ||
                  '确认后由本地服务上传图片与 Spine 到 OBS，替换地址后再写入数据库。'}
              </p>
              <button
                type="button"
                disabled={syncing || result.changes.length === 0}
                onClick={confirmSync}
              >
                {syncing
                  ? '正在更新'
                  : `更新全部变化（${result.changes.length}）`}
              </button>
            </footer>
          </div>
        )}
      </article>}

      {catalogs
        .filter((catalog) => catalog.catalogKey !== 'hero' || catalog.adapterKey === 'generic-json-v1')
        .map((catalog) => (
          <article
            className={`catalog-source-card${catalog.adapterKey === 'generic-json-v1' ? '' : ' catalog-source-card-compact'}`}
            key={catalog.catalogKey}
          >
            <header>
              <div className="catalog-source-title">
                <CatalogIconUpload gameKey={gameKey} catalog={catalog} onSaved={(iconUrl) => updateAdditionalCatalog(catalog.catalogKey, (current) => ({ ...current, iconUrl }))} />
                <div>
                  {renamingCatalogKey === catalog.catalogKey ? (
                    <form className="catalog-rename-form" onSubmit={(event) => { event.preventDefault(); void renameCatalog(catalog); }}>
                      <input autoFocus aria-label="图鉴名称" value={catalogNameDraft} maxLength={120} disabled={catalogRenameBusy} onChange={(event) => setCatalogNameDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Escape') setRenamingCatalogKey(''); }} />
                      <button type="submit" disabled={catalogRenameBusy || !catalogNameDraft.trim()}>保存</button>
                      <button type="button" disabled={catalogRenameBusy} onClick={() => setRenamingCatalogKey('')}>取消</button>
                      {catalogRenameError && <small className="catalog-rename-error" role="alert">{catalogRenameError}</small>}
                    </form>
                  ) : (
                    <div className="catalog-name-row">
                      <h3>{catalog.name}</h3>
                      {catalog.adapterKey === 'generic-json-v1' && <button className="catalog-rename-trigger" type="button" aria-label={`修改${catalog.name}名称`} title="修改图鉴名称" onClick={() => { setRenamingCatalogKey(catalog.catalogKey); setCatalogNameDraft(catalog.name); setCatalogRenameError(''); }}><Pencil aria-hidden="true" /></button>}
                    </div>
                  )}
                  <p>
                    {catalog.displayMode === 'standalone'
                      ? '独立页面'
                      : '弹窗页面'}{' '}
                    · {catalog.sourceUrl ? '已配置数据源' : '待配置数据源'}
                  </p>
                </div>
              </div>
              <div className="catalog-card-actions">
                <div className="catalog-template-button-wrap">
                  <button
                    className="catalog-history-button"
                    type="button"
                    onClick={() => setTemplateModalKey(catalog.catalogKey)}
                  >
                    模板设置
                  </button>
                  {savedCatalogsRef.current[catalog.catalogKey]?.settings.updatePolicy === 'custom' && catalog.hasIndependentCode && (
                    <span>独立</span>
                  )}
                </div>
                {catalog.adapterKey === 'generic-json-v1' && (
                  <>
                    <button className="catalog-history-button" type="button" onClick={() => void openLatestGenericTask(catalog)}>待确认任务</button>
                    <button className="catalog-history-button" type="button" onClick={() => void loadGenericHistory(catalog)}><History />更新记录</button>
                  </>
                )}
                <button
                  className="catalog-history-button catalog-delete-button"
                  type="button"
                  onClick={() => {
                    setCatalogDeleteError('');
                    setCatalogDeleteTarget(catalog);
                  }}
                >
                  <Trash2 />
                  删除图鉴
                </button>
              </div>
            </header>
            <label className="catalog-source-field">
              <span>图鉴列表 JSON 地址</span>
              <input
                type="url"
                placeholder="创建后在这里填写数据源地址"
                value={catalog.sourceUrl}
                disabled={additionalCatalogSaving === catalog.catalogKey}
                onChange={(event) =>
                  updateAdditionalCatalog(catalog.catalogKey, (current) => ({
                    ...current,
                    sourceUrl: event.target.value,
                  }))
                }
              />
              <button
                type="button"
                disabled={additionalCatalogSaving === catalog.catalogKey}
                onClick={() => void saveAdditionalCatalog(catalog)}
              >
                {additionalCatalogSaving === catalog.catalogKey ? '保存中…' : '保存地址'}
              </button>
              <button
                type="button"
                disabled={checkingCatalogKey === catalog.catalogKey || !catalog.sourceUrl.trim()}
                onClick={() => void checkCatalogUpdates(catalog)}
              >
                {checkingCatalogKey === catalog.catalogKey ? '检查中…' : '检查更新'}
              </button>
            </label>
            {additionalCatalogMessage.catalogKey === catalog.catalogKey && additionalCatalogMessage.text && <p className="catalog-source-feedback" role="status">{additionalCatalogMessage.text}</p>}
            {catalogCheckMessage[catalog.catalogKey] && <p className="catalog-source-feedback" role="status">{catalogCheckMessage[catalog.catalogKey]}</p>}
            {catalog.adapterKey === 'generic-json-v1' && !catalogCheckMessage[catalog.catalogKey] && (
              <div className="catalog-check-empty"><RefreshCw /><strong>等待检查资料源</strong><p>检查会先核对清单，只读取变化条目的详情，不会修改现有数据。</p></div>
            )}
          </article>
        ))}

      {catalogDeleteTarget && (
        <div
          className="catalog-add-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !catalogDeleteBusy)
              setCatalogDeleteTarget(null);
          }}
        >
          <section
            className="catalog-add-modal catalog-delete-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="catalog-delete-title"
          >
            <header>
              <div>
                <small>DELETE CATALOG</small>
                <h3 id="catalog-delete-title">确认删除「{catalogDeleteTarget.name}」？</h3>
              </div>
              <button
                type="button"
                aria-label="关闭"
                disabled={catalogDeleteBusy}
                onClick={() => setCatalogDeleteTarget(null)}
              >
                <X />
              </button>
            </header>
            <div className="catalog-add-template-step catalog-delete-warning">
              <p>删除后，该图鉴配置、已导入的图鉴条目和对应模板设置会被永久移除。</p>
              {catalogDeleteTarget.catalogKey === 'hero' && (
                <p>英雄图鉴还会删除此游戏的全部英雄资料、版本历史、同步记录和素材索引。此操作无法撤销。</p>
              )}
              {catalogDeleteError && <p role="alert">{catalogDeleteError}</p>}
            </div>
            <footer>
              <button
                className="is-secondary"
                type="button"
                disabled={catalogDeleteBusy}
                onClick={() => setCatalogDeleteTarget(null)}
              >
                取消
              </button>
              <button
                className="catalog-delete-confirm"
                type="button"
                disabled={catalogDeleteBusy}
                onClick={() => void deleteCatalog()}
              >
                {catalogDeleteBusy ? '正在删除…' : '确认删除'}
              </button>
            </footer>
          </section>
        </div>
      )}

      {genericCheck && (
        <div className="catalog-add-modal-backdrop" role="presentation">
          <section className="catalog-add-modal catalog-template-modal" role="dialog" aria-modal="true" aria-labelledby="generic-check-title">
            <header><h3 id="generic-check-title">{genericCheck.catalog.name} · 检查结果</h3></header>
            <div className="catalog-add-template-step">
              <p>来源 {genericCheck.result.sourceCount} 条，当前已入库 {genericCheck.result.currentCount} 条；新增 {genericCheck.result.summary.added} 条，变化 {genericCheck.result.summary.updated} 条。</p>
              <p>只会更新勾选的条目。未出现在 JSON 中的现有条目会保留。</p>
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {genericCheck.result.changes.filter((item) => item.type !== 'unchanged').map((item) => (
                  <label key={item.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0' }}>
                    <input type="checkbox" checked={genericSelected.includes(item.id)} onChange={(event) => setGenericSelected((current) => event.target.checked ? [...current, item.id] : current.filter((id) => id !== item.id))} />
                    <span>{item.name}（{item.id}） · {item.type === 'added' ? '新增' : '更新'}</span>
                  </label>
                ))}
              </div>
              {genericError && <p role="alert">{genericError}</p>}
            </div>
            <footer>
              <button type="button" disabled={genericBusy} onClick={() => setGenericCheck(null)}>取消</button>
              <button type="button" disabled={genericBusy || genericSelected.length === 0 || genericSelected.length > 200} onClick={() => void applyGeneric()}>{genericBusy ? '入库中…' : `确认更新（${genericSelected.length}）`}</button>
            </footer>
          </section>
        </div>
      )}
      {genericTask && (
        <div className="catalog-add-modal-backdrop" role="presentation">
          <section className="catalog-add-modal catalog-template-modal" role="dialog" aria-modal="true" aria-labelledby="generic-task-title">
            <header><h3 id="generic-task-title">{genericTask.catalog.name} · 自动更新任务</h3></header>
            <div className="catalog-add-template-step">
              <p>状态：{genericTask.task.status === 'awaiting_confirmation' ? '待人工确认' : genericTask.task.status === 'completed' ? '已完成' : genericTask.task.status === 'no_change' ? '无变化' : genericTask.task.status === 'failed' ? '失败' : '处理中'}</p>
              {genericTask.task.errorMessage && <p role="alert">{genericTask.task.errorMessage}</p>}
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {genericTask.task.result.map((item) => (
                  <div key={item.sourceId} style={{ padding: '8px 0' }}>
                    <label style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <input type="checkbox" disabled={genericTask.task.status !== 'awaiting_confirmation' || !['added', 'updated'].includes(item.type)} checked={genericTaskSelected.includes(item.sourceId)} onChange={(event) => setGenericTaskSelected((current) => event.target.checked ? [...current, item.sourceId] : current.filter((id) => id !== item.sourceId))} />
                      <span>{item.name}（{item.sourceId}） · {{ added: '新增', updated: '更新', source_missing: '来源缺失', mapping_error: '映射异常' }[item.type]}</span>
                    </label>
                    {item.reason && <p>{item.reason}</p>}
                    {!!item.detail && <button type="button" onClick={() => setGenericTaskJSON(item)}>查看单角色 JSON</button>}
                    {!!item.resources?.length && <div>NAS 路径：{item.resources.map((path) => <div key={path}>{path}</div>)}</div>}
                  </div>
                ))}
              </div>
              {genericError && <p role="alert">{genericError}</p>}
            </div>
            <footer>
              <button type="button" onClick={() => setGenericTask(null)}>关闭</button>
              {genericTask.task.status === 'awaiting_confirmation' && <button type="button" disabled={genericBusy || genericTaskSelected.length === 0 || genericTaskSelected.length > 200} onClick={() => void confirmGenericTask()}>{genericBusy ? '提交中…' : `确认更新（${genericTaskSelected.length}）`}</button>}
            </footer>
          </section>
          {genericTaskJSON && <section className="catalog-add-modal catalog-template-modal" role="dialog" aria-modal="true" aria-label="单角色 JSON"><header><h3>{genericTaskJSON.name} · JSON</h3></header><pre style={{ maxHeight: 480, overflow: 'auto' }}>{JSON.stringify(genericTaskJSON.detail, null, 2)}</pre><footer><button type="button" onClick={() => setGenericTaskJSON(null)}>关闭 JSON</button></footer></section>}
        </div>
      )}
      {genericHistory && (
        <div className="catalog-add-modal-backdrop" role="presentation">
          <section className="catalog-add-modal" role="dialog" aria-modal="true" aria-labelledby="generic-history-title">
            <header><h3 id="generic-history-title">{genericHistory.catalog.name} · 更新记录</h3></header>
            <div className="catalog-add-template-step">
              {genericHistory.items.length === 0 && <p>暂无更新记录</p>}
              {genericHistory.items.map((item, index) => (
                <div key={item.batchId} style={{ display: 'flex', gap: 12, justifyContent: 'space-between', padding: '8px 0' }}>
                  <span>{new Date(item.createdAt).toLocaleString()} · {item.count} 条 {item.rolledBackAt ? '· 已回滚' : ''}</span>
                  {!item.rolledBackAt && genericHistory.items.findIndex((batch) => !batch.rolledBackAt) === index && <button type="button" disabled={genericBusy} onClick={() => void rollbackGeneric(item.batchId)}>回滚</button>}
                </div>
              ))}
              {genericError && <p role="alert">{genericError}</p>}
            </div>
            <footer><button type="button" disabled={genericBusy} onClick={() => setGenericHistory(null)}>关闭</button></footer>
          </section>
        </div>
      )}
      {cardImport && (
        <div className="catalog-add-modal-backdrop" role="presentation">
          <section className="catalog-add-modal" role="dialog" aria-modal="true" aria-labelledby="card-import-title">
            <header><h3 id="card-import-title">确认卡牌映射入库</h3></header>
            <div className="catalog-add-template-step">
              <p>已检查 {cardImport.count} 张卡牌。确认后将写入当前数据库，保存列表摘要、原始 ID 与数据库条目 ID、详情 JSON 地址的映射。</p>
              <p>已有卡牌保留数据库 ID；列表中缺失的条目不会删除。不会批量加载卡牌详情。</p>
              {cardImportError && <p role="alert">{cardImportError}</p>}
            </div>
            <footer>
              <button type="button" disabled={cardImportBusy} onClick={() => setCardImport(null)}>取消</button>
              <button type="button" disabled={cardImportBusy} onClick={() => void confirmCardImport()}>{cardImportBusy ? '入库中…' : '确认入库'}</button>
            </footer>
          </section>
        </div>
      )}
      {templateModalKey && templateModalSettings && (
        <div className="catalog-add-modal-backdrop" role="presentation">
          <section
            className="catalog-add-modal catalog-template-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="catalog-template-modal-title"
          >
            <header>
              <div>
                <small>CATALOG TEMPLATE</small>
                <h3 id="catalog-template-modal-title">
                  {templateModalKey === 'hero'
                    ? '英雄图鉴模板设置'
                    : `${templateModalCatalog?.name || '图鉴'}模板设置`}
                </h3>
              </div>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setTemplateModalKey(null)}
              >
                <X />
              </button>
            </header>
            <div className="catalog-add-template-step">
              {templateModalCatalog?.adapterKey === 'generic-json-v1' && (
                <label>
                  <span>条目详情打开方式</span>
                  <output className="catalog-detail-mode-value">
                    {templateModalCatalog.displayMode === 'standalone' ? '独立页面' : '二级弹窗'}
                  </output>
                  <small>创建图鉴时确定，之后不可更改。</small>
                </label>
              )}
              {templateModalCatalog && (
                (templateModalCatalog.adapterKey === 'generic-json-v1' && !(gameKey === 'bangdream' && templateModalCatalog.catalogKey === 'catalog_8d4fbe12ff61416bbf1d8691fe899fa3')) ||
                (gameKey === 'arcanatactics' && (
                  templateModalCatalog.catalogKey === 'hero' ||
                  ['catalog_65fcfc5c37224d95987a03711aec9e67', 'catalog_ab547affe0d548a4bcdea0fb4ee1d12e'].includes(templateModalCatalog.catalogKey)
                ))
              ) && (
                <label>
                  <span>列表卡片形状</span>
                  <select
                    value={templateModalCatalog.settings.listShape || 'square'}
                    disabled={additionalCatalogSaving === templateModalCatalog.catalogKey}
                    onChange={(event) => {
                      const listShape = event.target.value as NonNullable<CatalogTemplateSettings['listShape']>;
                      if (gameKey === 'arcanatactics' && templateModalCatalog.catalogKey === 'hero') {
                        setTemplateSettings((current) => ({ ...current, listShape }));
                      }
                      updateAdditionalCatalog(templateModalCatalog.catalogKey, (current) => ({
                        ...current,
                        settings: { ...current.settings, listShape },
                      }));
                      void saveCatalogShape(templateModalCatalog, listShape);
                    }}
                  >
                    <option value="square">正方形</option>
                    <option value="rectangle">长方形</option>
                    {templateModalCatalog.adapterKey === 'generic-json-v1' && <option value="landscape">长方形（横版）</option>}
                  </select>
                  <small>横版每排显示 4 个，WAP 端每排显示 3 个；其他形状保持原有布局。</small>
                </label>
              )}
              <label>
                <span>选择模板</span>
                <select
                  value={templateModalSettings.detailTemplate}
                  disabled={
                    templateLoading ||
                    templateModalKey !== 'hero' ||
                    isTemplateForkLocked
                  }
                  onChange={(event) => {
                    const detailTemplate = event.target.value;
                    const shared = sharedTemplates.find(
                      (template) => template.templateKey === detailTemplate,
                    );
                    setTemplateSettings((current) => ({
                      ...current,
                      detailTemplate,
                      ...(shared
                        ? { templateSourceGame: shared.sourceGameKey }
                        : { templateSourceGame: undefined }),
                    }));
                  }}
                >
                  <option value="default">通用模板</option>
                  {templateModalKey === 'hero' && (
                    <>
                      {sharedTemplates.map((template) => (
                        <option
                          value={template.templateKey}
                          key={template.templateKey}
                        >
                          通用模板 · {template.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <small>决定该图鉴详情内容使用的展示结构。</small>
              </label>
              <label>
                <span>模板维护方式</span>
                <select
                  value={templateModalSettings.updatePolicy}
                  disabled={templateLoading || isTemplateForkLocked}
                  onChange={(event) => {
                    const updatePolicy = event.target
                      .value as CatalogTemplateSettings['updatePolicy'];
                    if (templateModalKey === 'hero') {
                      setTemplateSettings((current) => ({
                        ...current,
                        updatePolicy,
                      }));
                    } else if (templateModalCatalog) {
                      updateAdditionalCatalog(
                        templateModalCatalog.catalogKey,
                        (current) => ({
                          ...current,
                          settings: { ...current.settings, updatePolicy },
                        }),
                      );
                    }
                  }}
                >
                  <option value="follow">跟随模板版本</option>
                  <option
                    value="custom"
                    disabled={templateModalKey === 'hero' && !hasCustomTemplate}
                  >
                    独立维护一套代码
                    {templateModalKey === 'hero' && !hasCustomTemplate
                      ? '（未生成专属分支）'
                      : ''}
                  </option>
                </select>
                <small>评论、登录、消息和安全修复仍由平台统一更新。</small>
              </label>
            </div>
            {isTemplateForkLocked && (
              <div className="catalog-template-lock-note">
                <strong>当前模板已进入独立维护</strong>
                <span>
                  基础模板和维护方式已锁定。如需更换，应通过模板迁移或重建专属分支处理，避免覆盖当前游戏的定制代码。
                </span>
              </div>
            )}
            {(templateModalKey === 'hero'
              ? templateMessage
              : additionalCatalogMessage.catalogKey === templateModalKey ? additionalCatalogMessage.text : '') && (
              <p className="catalog-template-modal-message">
                {templateModalKey === 'hero'
                  ? templateMessage
                  : additionalCatalogMessage.text}
              </p>
            )}
            {saveSharedOpen && (
              <div className="catalog-save-shared-form">
                <label>
                  <span>新通用模板名称</span>
                  <input
                    autoFocus
                    value={sharedTemplateName}
                    placeholder="例如：卡牌式角色详情模板"
                    onChange={(event) =>
                      setSharedTemplateName(event.target.value)
                    }
                  />
                </label>
                <button
                  type="button"
                  disabled={sharedTemplateSaving || !sharedTemplateName.trim()}
                  onClick={() => void saveAsSharedTemplate()}
                >
                  {sharedTemplateSaving ? '保存中…' : '确认保存'}
                </button>
              </div>
            )}
            {sharedTemplateError && (
              <p className="catalog-template-modal-message">
                {sharedTemplateError}
              </p>
            )}
            <footer>
              <button
                type="button"
                className="catalog-save-as-template"
                disabled={templateModalSettings.updatePolicy !== 'custom'}
                onClick={() => {
                  setSaveSharedOpen((current) => !current);
                  setSharedTemplateError('');
                }}
              >
                保存为新的通用模板
              </button>
              <span className="catalog-modal-footer-spacer" />
              <button
                type="button"
                className="is-secondary"
                onClick={() => setTemplateModalKey(null)}
              >
                取消
              </button>
              <button
                type="button"
                disabled={
                  templateSaving ||
                  (templateModalCatalog
                    ? additionalCatalogSaving ===
                      templateModalCatalog.catalogKey
                    : false)
                }
                onClick={() => {
                  if (templateModalKey === 'hero') {
                    void saveTemplateSettings();
                  } else if (templateModalCatalog) {
                    void saveAdditionalCatalog(templateModalCatalog);
                  }
                }}
              >
                {templateSaving || additionalCatalogSaving
                  ? '保存中…'
                  : '保存模板设置'}
              </button>
            </footer>
          </section>
        </div>
      )}

      {addCatalogOpen && (
        <div className="catalog-add-modal-backdrop" role="presentation">
          <section
            className="catalog-add-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="catalog-add-title"
          >
            <header>
              <div>
                <small>NEW CATALOG</small>
                <h3 id="catalog-add-title">新增图鉴</h3>
              </div>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setAddCatalogOpen(false)}
              >
                <X />
              </button>
            </header>

            <div className="catalog-add-form">
              <label>
                <span>图鉴名称</span>
                <input
                  value={newCatalog.name}
                  placeholder="请输入图鉴名称"
                  onChange={(event) =>
                    setNewCatalog((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                <span>条目详情打开方式</span>
                <select
                  value={newCatalog.displayMode}
                  onChange={(event) =>
                    setNewCatalog((current) => ({
                      ...current,
                      displayMode: event.target.value as 'standalone' | 'modal',
                    }))
                  }
                >
                  <option value="standalone">独立页面</option>
                  <option value="modal">弹窗页面</option>
                </select>
              </label>
              <label>
                <span>列表卡片形状</span>
                <select
                  value={newCatalog.settings.listShape}
                  onChange={(event) => setNewCatalog((current) => ({
                    ...current,
                    settings: { listShape: event.target.value as NonNullable<CatalogTemplateSettings['listShape']> },
                  }))}
                >
                  <option value="square">正方形</option>
                  <option value="rectangle">长方形</option>
                  <option value="landscape">长方形（横版）</option>
                </select>
                <small>横版每排显示 4 个，WAP 端每排显示 3 个。</small>
              </label>
              <small className="catalog-add-hint">
                创建完成后，可在图鉴卡片中填写 JSON 数据源并设置详情模板。
              </small>
            </div>

            {addCatalogError && (
              <p className="catalog-add-error">{addCatalogError}</p>
            )}
            <footer>
              <button
                type="button"
                className="is-secondary"
                onClick={() => setAddCatalogOpen(false)}
              >
                取消
              </button>
              <button
                type="button"
                disabled={addCatalogSaving || !newCatalog.name.trim()}
                onClick={() => void createCatalog()}
              >
                {addCatalogSaving ? '正在创建…' : '创建图鉴'}
              </button>
            </footer>
          </section>
        </div>
      )}
    </section>
  );
}
